import AbstractFormulaNode from "./AbstractFormulaNode.js";
import NaFormulaNode from "./NaFormulaNode.js";

/**
 * `AbstractBinaryOperatorFormulaNode` is an abstract implementation of
 * `abstractFormulaNode` representing all binary operators (with
 * left and right operands).
 *
 * @abstract
 * @param {abstractFormulaNode} spec.leftOperand - left-hand child node
 * @param {abstractFormulaNode} spec.rightOperand - right-hand child node
 */
export default class AbstractBinaryOperatorFormulaNode extends AbstractFormulaNode {
	constructor(spec = {}) {
		super(spec);

		this._leftOperand = spec.leftOperand;
		this._rightOperand = spec.rightOperand;
	}

	getLeftOperand() {
		return this._leftOperand;
	}

	getRightOperand() {
		return this._rightOperand;
	}

	forEach(fn) {
		super.forEach(fn);
		this.getLeftOperand().forEach(fn);
		this.getRightOperand().forEach(fn);
	}

	accept(visitor, additionalInformation) {
		return visitor.visitBinaryOperatorNode(this, additionalInformation);
	}

	expandFormula(report) {
		const leftOperand =
			this._leftOperand && this._leftOperand.expandFormula(report);
		const rightOperand =
			this._rightOperand && this._rightOperand.expandFormula(report);
		return new this.constructor({
			leftOperand,
			rightOperand,
		});
	}

	hasReferenceTo(item) {
		return (
			this._leftOperand.hasReferenceTo(item) ||
			this._rightOperand.hasReferenceTo(item)
		);
	}

	validate() {
		this._leftOperand.validate(...arguments);
		this._rightOperand.validate(...arguments);
	}

	add(node) {
		return node.addToBinaryOperator(this);
	}

	addToBinaryOperator(node) {
		return new this.constructor({
			leftOperand: node,
			rightOperand: new NaFormulaNode(),
		});
	}

	addToSum(node) {
		return node.addToBinaryOperator(this);
	}

	removeLastNode() {
		return this._rightOperand.removeLastNodeFrom(this);
	}

	removeLastNodeFrom(node) {
		return node.getClass()({
			leftOperand: node.getLeftOperand(),
			rightOperand: this.removeLastNode(),
		});
	}

	_serializedProperties() {
		const properties = super._serializedProperties();

		properties.leftOperand = this._leftOperand;
		properties.rightOperand = this._rightOperand;

		return properties;
	}

	static transformMaterializationData(data, context) {
		super.transformMaterializationData(...arguments);

		data.leftOperand = context.factory.materialize(
			data.leftOperand,
			context,
		);

		data.rightOperand = context.factory.materialize(
			data.rightOperand,
			context,
		);
	}
}
